import {
  Flex,
  Spacer,
  Skeleton,
  Box,
  Text,
  Heading,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useState, useMemo, useEffect } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import {
  CreditAgreement,
  Insights,
  Util,
} from '@finley-technologies/finley-types';

import { CreditFacilityProfitVintageInputInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartDatePicker } from '../shared/PortfolioChartDatePicker';
import {
  chartsInitialEndDateJs,
  ChartTimePeriod,
  cohortCadenceLabelFormatter,
  CohortConfig,
  defaultCohortCadence,
  defaultMaxSeriesAge,
  formatCohortCadenceSelectOptions,
  onPortfolioVintageChartFilterUpdate,
  onSelectDateRange,
  onToggleActiveField,
  PortfolioChartConfig,
  ProfitsVintageInsight,
  ProfitsVintageTableRow,
  vintageInitialStartDate,
  vintageInitialStartDateJs,
} from '../shared/portfolioChartUtils';
import { PortfolioVintageChartCards } from '../shared/PortfolioVintageChartCards';
import { PortfolioVintageReChart } from '../shared/PortfolioVintageReChart';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import { getDefaultDashboardDisplayEndDate } from 'packages/common-frontend/constants';
import { formatBaseFieldValue } from 'packages/common-frontend/utils';
import {
  GetProfitVintageInsightsQueryGQLType,
  useGetProfitVintageInsightsQuery,
} from 'packages/portfolio/requests/requests.gql.generated';
import { PortfolioDownloadCsvLink } from 'packages/portfolio/ui/shared/PortfolioDownloadCsvLink';

const ProfitLossVintageTable: React.FC<{
  tableData: ProfitsVintageTableRow[];
  selectedCohortConfig: CohortConfig;
  maxSeriesAge: number;
  fieldType: CreditAgreement.DataTapeField.Type;
}> = ({ tableData, selectedCohortConfig, maxSeriesAge, fieldType }) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns: ColumnDef<any, any>[] = [
    {
      id: 'origination',
      accessorKey: 'originationDate',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Origination
          </Text>
        );
      },
      // eslint-disable-next-line react/display-name
      cell: ({ row, getValue }) => {
        const originationDate = cohortCadenceLabelFormatter(
          selectedCohortConfig.cohortBaseCadence,
          getValue(),
        );
        return (
          <Flex alignItems="center">
            <Box
              height="16px"
              width="16px"
              mr="10px"
              bgColor={row.original.color}
            />
            <Text>{originationDate}</Text>
          </Flex>
        );
      },
      size: 200,
    },
    ...Array(maxSeriesAge)
      .fill(0)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      .map((_, cohortAge) => {
        const columnId = `${cohortAge} ${
          selectedCohortConfig.cohortSeriesCadence
        }${cohortAge === 1 ? '' : 's'}`;
        return {
          id: columnId,
          accessorKey: columnId,
          /* eslint-disable react/display-name */
          header: () => {
            return (
              <Text color="black" fontSize="16px">
                {columnId}
              </Text>
            );
          },
          cell: ({ getValue }: { getValue: any }) => {
            if (getValue() === undefined) {
              return <></>;
            }
            return formatBaseFieldValue(getValue(), fieldType);
          },
          size: 140,
        };
      }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} frozenColumnIds={['origination']} />;
};

export const ProfitLossVintageChartTemplate: React.FC<{
  creditFacilityId: string;
  chartTitle: string;
  field: keyof Insights.ProfitGeneratedCohortInsightsData;
  fieldType: CreditAgreement.DataTapeField.Type;
  yAxisLabel?: string;
}> = ({ creditFacilityId, chartTitle, field, fieldType, yAxisLabel }) => {
  const [
    selectedDatapoint,
    setSelectedDatapoint,
  ] = useState<ProfitsVintageInsight | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: vintageInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [
    profitVintageInputDataForDateRange,
    setProfitVintageInputDataForDateRange,
  ] = useState<CreditFacilityProfitVintageInputInsightsGqlDataGQLType[]>([]);
  const [chartDataWithCohortCadence, setChartDataWithCohortCadence] = useState<
    ProfitsVintageInsight[]
  >([]);
  const [tableDataByOriginationDate, setTableDataByOriginationDate] = useState<
    ProfitsVintageTableRow[]
  >([]);

  const [activeFields, setActiveFields] = useState<
    Partial<keyof ProfitsVintageInsight>[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  // Cohort-specific states, powers chart filters
  const [
    selectedCohortConfig,
    setSelectedCohortConfig,
  ] = useState<CohortConfig>({
    cohortBaseCadence: defaultCohortCadence,
    cohortSeriesCadence: defaultCohortCadence,
  });
  const [selectedMaxSeriesAge, setSelectedMaxSeriesAge] = useState<number>(
    defaultMaxSeriesAge,
  );

  const cohortCadenceSelectOptions = useMemo(
    () => formatCohortCadenceSelectOptions(),
    [],
  );

  // repayment data may not exist for borrower so don't show component if there's no data
  const doesDataForChartExist = chartDataWithCohortCadence.some(
    (cohortSeries) => {
      return Object.entries(cohortSeries).some(([key, value]) => {
        return key !== 'cohortSeriesAge' && value !== null;
      });
    },
  );

  // we generate new cohort data when filter selections are changed
  // using the base input daily cohort data for the selected dates
  // all chart filters for vintage aside from the date range picker are run on client without API calls
  useEffect(() => {
    onPortfolioVintageChartFilterUpdate({
      profitVintageInputDataForDateRange,
      selectedCohortConfig,
      selectedMaxSeriesAge,
      field,
      setChartConfig,
      setActiveFields,
      setChartDataWithCohortCadence,
      setTableDataByOriginationDate,
      setSelectedDatapoint,
    });
  }, [
    field,
    profitVintageInputDataForDateRange,
    selectedCohortConfig,
    selectedMaxSeriesAge,
  ]);

  const {
    refetch: refetchProfitVintageInsights,
    data: profitVintageInsightsData,
    loading: isProfitVintageInsightsLoading,
  } = useGetProfitVintageInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: vintageInitialStartDate,
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const inputData = data.getProfitVintageInsights.profitVintageInputData;

      setProfitVintageInputDataForDateRange(inputData);

      onPortfolioVintageChartFilterUpdate({
        profitVintageInputDataForDateRange: inputData,
        selectedCohortConfig,
        selectedMaxSeriesAge,
        field,
        setChartConfig,
        setActiveFields,
        setChartDataWithCohortCadence,
        setTableDataByOriginationDate,
        setSelectedDatapoint,
      });
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isProfitVintageInsightsLoading || !profitVintageInsightsData) {
    return <Skeleton height="700px" />;
  }

  if (!doesDataForChartExist) {
    return null;
  }

  const PortfolioChartCardsInstance = PortfolioVintageChartCards<ProfitsVintageInsight>();
  const PortfolioChartInstance = PortfolioVintageReChart<ProfitsVintageInsight>();

  const downloadFileHeaders =
    tableDataByOriginationDate.length > 0
      ? _.map(_.keys(tableDataByOriginationDate[0]), (headerKey) => {
          return {
            label: commonUtils.toTitleCase(headerKey),
            key: headerKey,
          };
        })
      : undefined;

  const formattedDownloadData = tableDataByOriginationDate.map((row) => {
    const newRow = { ...row };
    newRow.originationDate = cohortCadenceLabelFormatter(
      selectedCohortConfig.cohortBaseCadence,
      newRow.originationDate,
    ) as Util.FullDateStr;
    return newRow;
  });

  return (
    <Box mb="40px">
      <Flex
        backgroundColor="white"
        borderRadius="8px"
        borderWidth="1px"
        borderColor={colors.gray[200]}
        padding="40px"
        direction="column"
      >
        <Flex>
          <Box width="70%">
            <Heading as="h3" fontSize="20px" mb="24px">
              {chartTitle}
            </Heading>
            <SimpleGrid columns={2} spacing="24px" mb="40px">
              <Box
                border={`1px solid ${colors.gray[200]}`}
                borderRadius="8px"
                padding="16px"
              >
                <Text fontWeight={500} mb="16px">
                  Vintage
                </Text>

                <Select
                  value={selectedCohortConfig.cohortBaseCadence}
                  onChange={(cadence) =>
                    setSelectedCohortConfig({
                      ...selectedCohortConfig,
                      cohortBaseCadence: cadence ?? defaultCohortCadence,
                    })
                  }
                  options={cohortCadenceSelectOptions}
                />
                <Box h="16px" />
                <PortfolioChartDatePicker
                  onSelectDateRange={(
                    newDateRange: [Date | null, Date | null],
                  ) => {
                    return onSelectDateRange<GetProfitVintageInsightsQueryGQLType>(
                      {
                        newDateRange,
                        refetch: refetchProfitVintageInsights,
                        creditFacilityId,
                        setChartDataDateRange,
                      },
                    );
                  }}
                  currentDateRange={chartDataDateRange}
                  width="100%"
                />
              </Box>
              <Box
                border={`1px solid ${colors.gray[200]}`}
                borderRadius="8px"
                padding="16px"
              >
                <Text fontWeight={500} mb="16px">
                  Timeline
                </Text>
                {/* positive integers only */}
                <NumberInput
                  step={1}
                  defaultValue={12}
                  min={0}
                  precision={0}
                  mb="16px"
                  onChange={(value) => {
                    setSelectedMaxSeriesAge(_.toNumber(value));
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper>
                      <Icon
                        icon="caret-up"
                        type="solid"
                        color={colors.gray[400]}
                      />
                    </NumberIncrementStepper>
                    <NumberDecrementStepper>
                      <Icon
                        icon="caret-down"
                        type="solid"
                        color={colors.gray[400]}
                      />
                    </NumberDecrementStepper>
                  </NumberInputStepper>
                </NumberInput>
                <Select
                  value={selectedCohortConfig.cohortSeriesCadence}
                  onChange={(cadence) =>
                    setSelectedCohortConfig({
                      ...selectedCohortConfig,
                      cohortSeriesCadence: cadence ?? defaultCohortCadence,
                    })
                  }
                  options={cohortCadenceSelectOptions}
                />
              </Box>
            </SimpleGrid>
            <PortfolioChartInstance
              chartData={chartDataWithCohortCadence}
              chartTitle={chartTitle}
              selectedCohortSeriesCadence={
                selectedCohortConfig.cohortSeriesCadence
              }
              currentSelectedCohortSeriesAge={
                selectedDatapoint
                  ? selectedDatapoint.cohortSeriesAge
                  : defaultMaxSeriesAge
              }
              onSelectDatapoint={setSelectedDatapoint}
              chartConfig={chartConfig}
              visibleChartComponents={activeFields}
              yAxisLabel={yAxisLabel}
              showLegend={false}
            />
          </Box>
          <Spacer />
          {selectedDatapoint && (
            <Flex width="25%" flexDirection="column">
              <PortfolioChartCardsInstance
                selectedDatapoint={selectedDatapoint}
                chartConfig={chartConfig}
                cohortSeriesCadence={selectedCohortConfig.cohortSeriesCadence}
                activeFields={activeFields}
                onToggleActiveField={(
                  selectedField: keyof ProfitsVintageInsight,
                ) =>
                  onToggleActiveField<ProfitsVintageInsight>({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
                chartTitle={chartTitle}
              />
              <Spacer />

              {downloadFileHeaders && (
                <PortfolioDownloadCsvLink
                  dataRows={formattedDownloadData}
                  headers={downloadFileHeaders}
                  chartTitle={chartTitle}
                  selectedTimePeriod={
                    (selectedCohortConfig.cohortBaseCadence as unknown) as ChartTimePeriod
                  }
                />
              )}
            </Flex>
          )}
        </Flex>
        <Box mt="40px">
          <ProfitLossVintageTable
            tableData={tableDataByOriginationDate}
            selectedCohortConfig={selectedCohortConfig}
            maxSeriesAge={chartDataWithCohortCadence.length}
            fieldType={fieldType}
          />
        </Box>
      </Flex>
    </Box>
  );
};
